/* eslint-disable max-lines */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { formatDurationFromMinutes } from '@modernloop/shared/datetime';
import { useFlag } from '@modernloop/shared/feature-flag';
import { DurationFilledIcon, OrderLockIcon, OrderShuffleIcon, TrashIcon } from '@modernloop/shared/icons';
import { Alert, Button, IconButton, Stack, TextField, Tooltip, inputBaseClasses } from '@mui/material';
import { addMinutes, isAfter, parseISO } from 'date-fns';
import { isEmpty, uniq } from 'lodash';

import {
  AtsInterviewDefinition,
  DateTimeRangeInput,
  InterviewCardHeader_JobFragment,
  InterviewCardHeader_JobStageInterviewFragment,
  InterviewCardHeader_JobStageInterviewGroupsFragment,
  InterviewCardHeader_JobStageInterviewsFragment,
} from 'src/generated/mloop-graphql';

import DurationTimePicker from 'src/entities/InterviewPlan/DurationTimePicker';

import { useOrgAtsService } from 'src/hooks/atsService';
import { getAtsInterviewDefinitionFields } from 'src/hooks/atsService/util';
import useDisplayTimezoneValue from 'src/hooks/useDisplayTimezoneValue';

import { formatRange, startOfDay } from 'src/utils/dateUtils';

// eslint-disable-next-line modernloop/restrict-imports.cjs
import AtsScorecardSelect from 'src/views-new/Scorecards/AtsScorecardSelect';
// eslint-disable-next-line modernloop/restrict-imports.cjs
import TimezoneSelect from 'src/views-new/TimezoneSelect';

import { DEFAULT_INTERVIEW_NAME } from 'src/strings';

import { BrighthireButtonSelect } from '../Select/BrighthireSelect/ButtonSelect';
import { useIsBrighthireIntegrationEnabled } from '../Select/BrighthireSelect/useIsBrighthireIntegrationEnabled';
import CollaborationToolSelect from '../Select/CollaborationTool/index';

import CodingLink from './CodingLink';
import MoreMenuOptions, { InterviewMenuOptions } from './MoreMenuOptions';
import useWarningForStartAt from './useWarningForStartAt';

export enum InterviewOptions {
  EDIT_DETAILS = 'edit_details',
  MOVE_UP = 'move_up',
  MOVE_DOWN = 'move_down',
  GROUP_WITH_ABOVE = 'group_with_above',
  GROUP_WITH_BELOW = 'group_with_below',
}

type Props = {
  index: number; // This is index of slot in group
  schedulable: boolean;
  candidateAvailabilities?: DateTimeRangeInput[];
  onUpdated: (jobStageInterview: InterviewCardHeader_JobStageInterviewFragment) => void;
  onDelete: (jobStageInterviewId: string) => void;
  onMenuSelect: (option: InterviewMenuOptions) => void;
  atsSyncedInterviewFields?: boolean;
};

type Fragments = {
  job: InterviewCardHeader_JobFragment;
  jobStageInterview: InterviewCardHeader_JobStageInterviewFragment;
  jobStageInterviews: InterviewCardHeader_JobStageInterviewsFragment[];
  jobStageInterviewGroups: InterviewCardHeader_JobStageInterviewGroupsFragment[];
};

const InterviewCardHeader: FCWithFragments<Fragments, Props> = ({
  job,
  jobStageInterview,
  jobStageInterviews,
  jobStageInterviewGroups,
  index,
  schedulable,
  candidateAvailabilities,
  onUpdated,
  onDelete,
  onMenuSelect,
  atsSyncedInterviewFields = false,
}): JSX.Element => {
  const { t } = useTranslation();
  const atsService = useOrgAtsService();

  const displayTimezone = useDisplayTimezoneValue();
  const codingTemplateSelectEnabled = useFlag('prd-155-codesignal-template-select');
  const brighthireIntegrationEnabled = useIsBrighthireIntegrationEnabled();
  let jobStageInterviewEndTime: Date | null = null;
  jobStageInterviews.forEach((interview) => {
    if (!interview.forcedStartAt || !interview.duration) {
      return;
    }

    const { duration } = interview;

    if (!jobStageInterviewEndTime) {
      jobStageInterviewEndTime = parseISO(interview.forcedStartAt);
    }

    const interviewEndTime = addMinutes(parseISO(interview.forcedStartAt), duration);

    if (isAfter(interviewEndTime, jobStageInterviewEndTime)) {
      jobStageInterviewEndTime = interviewEndTime;
    }
  });

  const startAtWarning = useWarningForStartAt(
    { jobStageInterviews },
    { jobStageInterviewId: jobStageInterview.id, candidateAvailabilities }
  );

  const [hasNameChanged, setHasNameChanged] = useState(false);
  const [showDurationTimePicker, setShowDurationTimePicker] = useState(false);

  const name = useMemo(() => {
    return jobStageInterview.name;
  }, [jobStageInterview.name]);

  const uniqueCandidateAvailabilityDays = useMemo(() => {
    return uniq(
      candidateAvailabilities?.map((availability) => {
        return startOfDay(availability.startAt, displayTimezone).getTime();
      })
    );
  }, [candidateAvailabilities, displayTimezone]);

  const durationOptionsMenu = useRef<HTMLButtonElement>(null);

  const interviewSlotDuration = useMemo(() => {
    return jobStageInterview.duration || 45 * 60 * 1000;
  }, [jobStageInterview]);

  let durationText =
    formatDurationFromMinutes(interviewSlotDuration, { abbreviate: true }) ||
    t('common:duration.minutesShort', { count: interviewSlotDuration });

  if (jobStageInterview.forcedStartAt) {
    const startTime = parseISO(jobStageInterview.forcedStartAt);
    const endTime = addMinutes(startTime, interviewSlotDuration);
    durationText = `${formatRange(startTime, endTime, displayTimezone, 'MMM d', 'h:mm aaa')} · ${durationText}`;
  }

  const handlePlanOrderClick = () => {
    onUpdated({ ...jobStageInterview, isLockedOrder: !jobStageInterview.isLockedOrder });
  };

  const handleInterviewNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setHasNameChanged(true);
    onUpdated({
      ...jobStageInterview,
      name: event.target.value,
    });
  };

  const handleRemoveJobStageInterview = () => {
    onDelete(jobStageInterview.id);
  };

  const handleDurationTimepickerClose = () => {
    setShowDurationTimePicker(false);
  };

  const handleDurationTimeChange = useCallback(
    (duration: number, dateTime: Date | null) => {
      onUpdated({
        ...jobStageInterview,
        duration,
        forcedStartAt: dateTime?.toISOString() || jobStageInterview.forcedStartAt,
      });
    },
    [jobStageInterview, onUpdated]
  );

  const handleClearStartAt = () => {
    onUpdated({ ...jobStageInterview, forcedStartAt: undefined });
  };

  const handleAtsInterviewDefinitionSelect = (atsInterviewDefinition: AtsInterviewDefinition) => {
    let { name: interviewName } = jobStageInterview;

    if (!hasNameChanged) {
      interviewName = atsInterviewDefinition.name ?? DEFAULT_INTERVIEW_NAME;
    }

    onUpdated({
      ...jobStageInterview,
      atsInterviewDefinition: {
        atsId: atsInterviewDefinition.atsId,
        name: atsInterviewDefinition.name,
        atsJobId: atsInterviewDefinition.atsJobId,
        atsFields: atsInterviewDefinition.atsFields,
      },
      name: interviewName,
      duration:
        getAtsInterviewDefinitionFields(atsInterviewDefinition?.atsFields, 'estimatedMinutes') ||
        jobStageInterview.duration,
    });
  };

  const getStartAtForDurationTimePicker = (): Date => {
    if (jobStageInterviewEndTime) {
      return jobStageInterviewEndTime;
    }

    const availabilityStartTimes: Date[] =
      candidateAvailabilities && !isEmpty(candidateAvailabilities)
        ? candidateAvailabilities?.map((value) => parseISO(value.startAt)).sort((a, b) => a.getTime() - b.getTime())
        : [new Date()];

    const now = availabilityStartTimes[0];
    now.setSeconds(0);
    return now;
  };

  return (
    <>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="nowrap">
          <Tooltip
            title={
              jobStageInterview.isLockedOrder
                ? t('hiring-dashboard-interviewers:interviewers.plan.interview.lockedPosition')
                : t('hiring-dashboard-interviewers:interviewers.plan.interview.shufflePosition')
            }
          >
            <IconButton onClick={handlePlanOrderClick} data-testid="interview-card-header-locked-order-button">
              {jobStageInterview.isLockedOrder && <OrderLockIcon />}
              {!jobStageInterview.isLockedOrder && <OrderShuffleIcon />}
            </IconButton>
          </Tooltip>
          <TextField
            data-testid="interview-card-header-name-textfield"
            defaultValue={name}
            fullWidth
            inputProps={{ style: { fontWeight: 600 } }}
            sx={{
              '& fieldset': { border: 'none' },
              [`& .${inputBaseClasses.root}`]: {
                backgroundColor: 'transparent',
                border: '1px solid transparent',
                '&:hover': { border: (theme) => `1px solid ${theme.palette.borderState.default}` },
              },
            }}
            onChange={handleInterviewNameChange}
          />
          <MoreMenuOptions
            index={index}
            jobStageInterview={jobStageInterview}
            jobStageInterviewGroups={jobStageInterviewGroups}
            onMenuSelect={onMenuSelect}
          />
          <Tooltip
            title={
              atsSyncedInterviewFields
                ? t('hiring-dashboard-jobs:jobs.interviewPlan.atsSyncTooltip', { atsName: atsService?.name })
                : t('hiring-dashboard-interviewers:interviewers.plan.interview.deleteInterview')
            }
          >
            {/* The span keeps the tooltip alive on a disabled button, which emits no hover events. */}
            <span>
              <IconButton
                disabled={atsSyncedInterviewFields}
                onClick={handleRemoveJobStageInterview}
                data-testid="interview-card-header-delete-button"
              >
                <TrashIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="nowrap">
          <Tooltip
            title={
              atsSyncedInterviewFields
                ? t('hiring-dashboard-jobs:jobs.interviewPlan.atsSyncTooltip', {
                    atsName: atsService?.name,
                  })
                : t('hiring-dashboard-interviewers:interviewers.plan.durationLabel', { duration: durationText })
            }
          >
            <Stack>
              <Button
                ref={durationOptionsMenu}
                data-testid="interview-card-header-duration-button"
                size="small"
                startIcon={<DurationFilledIcon sx={{ color: 'warning.main' }} />}
                disabled={atsSyncedInterviewFields}
                onClick={() => setShowDurationTimePicker(true)}
              >
                {durationText}
              </Button>
            </Stack>
          </Tooltip>
          {atsService?.hasScorecards && (
            <AtsScorecardSelect
              atsJobId={job?.atsId}
              selectScorecard={handleAtsInterviewDefinitionSelect}
              selectedScorecardName={jobStageInterview?.atsInterviewDefinition?.name}
              disabled={atsSyncedInterviewFields}
              disabledReason={t('hiring-dashboard-jobs:jobs.interviewPlan.atsSyncTooltip', {
                atsName: atsService?.name,
              })}
            />
          )}
          {brighthireIntegrationEnabled && (
            <BrighthireButtonSelect
              jobId={job.id}
              interviewGuide={jobStageInterview.interviewGuide}
              onUpdated={(guide) => onUpdated({ ...jobStageInterview, interviewGuide: guide })}
            />
          )}
          {codingTemplateSelectEnabled && (
            <CollaborationToolSelect
              collaborationLinkType={jobStageInterview.collaborationLinkTypes?.[0]}
              onUpdated={(template) => onUpdated({ ...jobStageInterview, collaborationLinkTypes: template })}
            />
          )}
          {!codingTemplateSelectEnabled && (
            <CodingLink
              jobStageInterview={jobStageInterview}
              jobStageInterviewId={jobStageInterview.id}
              onUpdated={onUpdated}
            />
          )}
        </Stack>
        {startAtWarning && <Alert severity="warning">{startAtWarning}</Alert>}
      </Stack>

      {showDurationTimePicker && (
        <DurationTimePicker
          anchorEl={durationOptionsMenu}
          duration={interviewSlotDuration}
          utcTime={
            jobStageInterview.forcedStartAt
              ? parseISO(jobStageInterview.forcedStartAt)
              : getStartAtForDurationTimePicker()
          }
          timezone={displayTimezone}
          showTimePicker={Boolean(jobStageInterview.forcedStartAt)}
          utcAvailability={uniqueCandidateAvailabilityDays}
          maxDuration={720}
          timezoneSelectComponent={
            <TimezoneSelect openOnFocus fullWidth shouldUpdateDisplayTimezone value={displayTimezone} />
          }
          hideTimePicker={!schedulable}
          onChange={handleDurationTimeChange}
          clearTime={handleClearStartAt}
          onClose={handleDurationTimepickerClose}
        />
      )}
    </>
  );
};

InterviewCardHeader.fragments = {
  job: gql`
    fragment InterviewCardHeader_job on Job {
      id
      atsId
    }
  `,
  jobStageInterview: gql`
    ${CodingLink.fragments.jobStageInterview}
    ${CollaborationToolSelect.fragments.collaborationLinkType}
    ${BrighthireButtonSelect.fragments.interviewGuide}
    fragment InterviewCardHeader_jobStageInterview on JobStageInterview {
      id
      name
      isLockedOrder
      duration
      forcedStartAt @client
      atsInterviewDefinition {
        atsId
        name
        atsJobId
        atsFields {
          ... on GreenhouseInterviewDefinition {
            estimatedMinutes
            index
          }
        }
      }
      collaborationLinkTypes {
        ...CollaborationToolFilterPopover_collaborationLinkType
      }
      interviewGuide {
        ...BrighthireButtonSelect_interviewGuide
      }
      ...CodingLink_jobStageInterview
      ...MoreMenuOptions_jobStageInterview
    }
  `,
  jobStageInterviews: gql`
    ${useWarningForStartAt.fragments.jobStageInterviews}
    fragment InterviewCardHeader_jobStageInterviews on JobStageInterview {
      id
      duration
      forcedStartAt @client
      atsInterviewDefinition {
        atsId
        atsFields {
          ... on GreenhouseInterviewDefinition {
            estimatedMinutes
            index
          }
        }
      }
      ...useWarningForStartAt_jobStageInterviews
    }
  `,
  jobStageInterviewGroups: gql`
    ${MoreMenuOptions.fragments.jobStageInterviewGroups}
    fragment InterviewCardHeader_jobStageInterviewGroups on JobStageInterviewGroup {
      id
      ...MoreMenuOptions_jobStageInterviewGroups
    }
  `,
};

export default InterviewCardHeader;
